import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { productsAPI, dipAPI } from '@/utils/api';
import { Plus, CheckCircle2, Circle, Beaker, AlertCircle, Crown, Package } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dipStatuses, setDipStatuses] = useState({});
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const [res, planRes] = await Promise.all([
        productsAPI.getAll(user.id || 'demo-user'),
        fetch(`${API_BASE}/api/user/plan?user_id=${user.id || 'demo-user'}`).then(r => r.json())
      ]);
      setProducts(res.data);
      setPlanData(planRes);

      const statuses = {};
      for (const product of res.data) {
        try {
          const statusRes = await dipAPI.getStatus(product.id);
          statuses[product.id] = statusRes.data;
        } catch (error) {
          console.error('Error loading status for', product.id);
        }
      }
      setDipStatuses(statuses);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (hasItem) => {
    return hasItem ? (
      <CheckCircle2 className="text-green-600" size={20} />
    ) : (
      <Circle className="text-muted-foreground" size={20} />
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">Dashboard</h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          Vue d'ensemble de vos produits et progression des DIP
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Quota Counter */}
        {planData && (
          <Card className={`slide-up ${planData.quota_exceeded ? 'border-red-300 bg-red-50/50' : 'border-primary/10'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                {planData.quota_exceeded ? <AlertCircle size={16} className="text-red-500" /> : <Package size={16} />}
                Utilisation — Plan {planData.plan === 'premium' ? 'Premium' : 'Gratuit'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Produits finis</span>
                  <span className={`font-semibold ${planData.usage.finished_products > planData.limits.finished_products ? 'text-red-600' : ''}`}>
                    {planData.usage.finished_products}/{planData.limits.finished_products === 999999 ? '∞' : planData.limits.finished_products}
                  </span>
                </div>
                <Progress value={planData.limits.finished_products === 999999 ? 10 : Math.min((planData.usage.finished_products / planData.limits.finished_products) * 100, 100)} 
                  className={`h-2 ${planData.usage.finished_products > planData.limits.finished_products ? '[&>div]:bg-red-500' : ''}`} />
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Matières premières</span>
                  <span className={`font-semibold ${planData.usage.raw_materials > planData.limits.raw_materials ? 'text-red-600' : ''}`}>
                    {planData.usage.raw_materials}/{planData.limits.raw_materials === 999999 ? '∞' : planData.limits.raw_materials}
                  </span>
                </div>
                <Progress value={planData.limits.raw_materials === 999999 ? 10 : Math.min((planData.usage.raw_materials / planData.limits.raw_materials) * 100, 100)} 
                  className={`h-2 ${planData.usage.raw_materials > planData.limits.raw_materials ? '[&>div]:bg-red-500' : ''}`} />
              </div>
              {planData.quota_exceeded && (
                <Button size="sm" className="w-full mt-2 bg-[#205F79] hover:bg-[#184a5e] gap-2" onClick={() => navigate('/subscription')}>
                  <Crown size={14} /> Passer au Premium
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200 slide-up">
          <CardHeader>
            <CardTitle className="text-2xl fade-in">{products.length}</CardTitle>
            <CardDescription>Produits totaux</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200 slide-up" style={{ animationDelay: '0.1s' }}>
          <CardHeader>
            <CardTitle className="text-2xl text-green-600 fade-in">
              {products.filter((p) => dipStatuses[p.id]?.dip_status === 'complete').length}
            </CardTitle>
            <CardDescription>DIP complets</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200 slide-up" style={{ animationDelay: '0.2s' }}>
          <CardHeader>
            <CardTitle className="text-2xl text-amber-600 fade-in">
              {products.filter((p) => dipStatuses[p.id]?.dip_status === 'incomplete').length}
            </CardTitle>
            <CardDescription>DIP en cours</CardDescription>
          </CardHeader>
        </Card>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-medium text-foreground">Mes produits</h2>
        <Button data-testid="btn-create-product" onClick={() => navigate('/products')} className="gap-2">
          <Plus size={18} />
          Nouveau produit
        </Button>
      </div>

      {products.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <div
              className="w-full h-64 mb-6 bg-cover bg-center rounded-lg opacity-60"
              style={{
                backgroundImage:
                  'url(https://images.unsplash.com/photo-1735352312517-d3c50bbce1f3?crop=entropy&cs=srgb&fm=jpg&q=85)',
              }}
            ></div>
            <h3 className="text-xl font-medium text-foreground mb-2">Aucun produit</h3>
            <p className="text-muted-foreground mb-6">Commencez par créer votre premier produit cosmétique</p>
            <Button onClick={() => navigate('/products')} className="gap-2">
              <Plus size={18} />
              Créer un produit
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {products.map((product) => {
            const status = dipStatuses[product.id] || {};
            return (
              <Card
                key={product.id}
                className="hover:border-primary/20 transition-all duration-200 cursor-pointer"
                onClick={() => navigate('/products')}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Beaker className="text-primary" size={24} strokeWidth={1.5} />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{product.name}</CardTitle>
                        <CardDescription>{product.type}</CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progression DIP</span>
                        <span className="font-medium">{status.completion_percentage || 0}%</span>
                      </div>
                      <Progress value={status.completion_percentage || 0} className="h-2" />
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status.has_formula)}
                        <span className="text-muted-foreground">Formule</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status.has_manufacturing)}
                        <span className="text-muted-foreground">Fabrication</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status.has_toxicology)}
                        <span className="text-muted-foreground">Toxicologie</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status.has_certificates)}
                        <span className="text-muted-foreground">Certificats</span>
                      </div>
                    </div>

                    {status.dip_status === 'complete' && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <CheckCircle2 className="text-green-600" size={18} />
                        <span className="text-sm text-green-800 font-medium">DIP complet et prêt</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
